import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { NotFound } from '@freelancer/404';
import { Auth } from '@freelancer/auth';
import { Datastore } from '@freelancer/datastore';
import { ProfileViewUsersCollection } from '@freelancer/datastore/collections';
import { Feature, FeatureFlagsService } from '@freelancer/feature-flags';
import { Location } from '@freelancer/location';
import { RedirectToPhp } from '@freelancer/redirect-to-php';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { toNumber } from '@freelancer/utils';
import { CookieService } from '@laurentgoudet/ngx-cookie';
import * as Rx from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable()
export class UserProfileGuardService implements CanActivate {
  constructor(
    private auth: Auth,
    private cookies: CookieService,
    private datastore: Datastore,
    private featureFlagService: FeatureFlagsService,
    private notFound: NotFound,
    private redirectToPhp: RedirectToPhp,
    private router: Router,
    private location: Location,
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    // TODO: Redirect to user profile not found or dashboard
    // Currently if `username` is not set in the url you are redirected to /freelancers.
    const username = route.paramMap.get('username');
    if (!username) {
      return this.redirectToPhp.doRedirect(state.url);
    }
    if (username.endsWith('.html')) {
      // FIXME: move that complexity to the location service by making
      // router.navigate return proper redirects with SSR
      this.location.navigateByUrl(
        this.router.serializeUrl(
          this.router.createUrlTree([`/u/${username.replace(/\.html$/, '')}`], {
            queryParams: route.queryParams,
          }),
        ),
      );
      return false;
    }

    const profileUser$ = this.datastore
      .collection<ProfileViewUsersCollection>('profileViewUsers', query =>
        query.where('username', 'equalsIgnoreCase', username),
      )
      .valueChanges()
      .pipe(map(users => users[0]));

    return Rx.combineLatest([
      this.auth.authState$,
      profileUser$,
      this.featureFlagService.getFlag(Feature.LEGACY_PROFILE_PAGE),
    ]).pipe(
      switchMap(([auth, profileUser, canViewLegacyPage]) => {
        // Could not fetch user profile
        if (!profileUser) {
          return this.notFound.render();
        }

        // TODO: T164083 this is a temp fix to redirect to legacy page. We need
        // to be handling this in the webapp. We already have a webapp page to
        // handle closed accounts, so these should not be redirected to legacy.
        if (!profileUser.isProfileVisible && !profileUser.closed) {
          return this.redirectToPhp.doRedirect(state.url);
        }

        // TODO: T81766 Remove when corporate portfolio editing has been implemented
        //  in webapp profile page.
        // If the users is attempting to edit own portfolio page redirect to the
        // legacy page.
        if (
          auth &&
          profileUser.id === toNumber(auth.userId) &&
          route.queryParams.page === 'portfolio'
        ) {
          return this.redirectToPhp.doRedirect(state.url);
        }

        // use ?w=t (webapp = true) to access the webapp version
        if (route.queryParams.w === 't') {
          return Rx.of(true);
        }
        // use ?w=f (webapp = false) to access the legacy version
        if (route.queryParams.w === 'f') {
          return this.redirectToPhp.doRedirect(state.url);
        }

        // refproject is reference to modify hireme on the user profile page
        // i.e. land on user profile with hireme pre-filled with details
        // TODO: Handle this legacy logic in the webapp
        if (parseInt(route.queryParams.refproject, 10) > 0) {
          return this.redirectToPhp.doRedirect(state.url);
        }

        // If the  user is logged out only show webapp version of page
        if (!auth) {
          return Rx.of(true);
        }

        // Exclude all corporate users, regardless of status,
        // from viewing their own editable profile in the webapp
        if (
          profileUser.id === toNumber(auth.userId) &&
          profileUser.corporate !== undefined
        ) {
          return this.redirectToPhp.doRedirect(state.url);
        }

        if (!canViewLegacyPage) {
          return Rx.of(true);
        }

        // Show webapp for enterprise builds
        if (this.uiConfig.theme) {
          return Rx.of(true);
        }

        // Support legacy robots tests with this cookie check while we're still
        // sending traffic to both webapp and legacy user profile pages.
        if (this.cookies.get('no_abtest') !== undefined) {
          return this.redirectToPhp.doRedirect(state.url);
        }

        return Rx.of(true);
      }),
    );
  }
}
